import { Injectable } from '@angular/core';
import { TokenResponse } from '../models/auth.models';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USERNAME_KEY = 'username';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const DIAL_CODE_KEY = 'dial_code';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  saveTokens(tokens: TokenResponse, username: string, rememberMe: boolean, dialCode?: string): void {
    const storage = this.getStorage(rememberMe);
    const expiry = Date.now() + tokens.expires_in * 1000;

    storage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    storage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    storage.setItem(USERNAME_KEY, username);
    storage.setItem(TOKEN_EXPIRY_KEY, String(expiry));
    if (dialCode?.trim()) {
      storage.setItem(DIAL_CODE_KEY, this.normalizeDialCode(dialCode));
    }

    if (!rememberMe) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(DIAL_CODE_KEY);
    } else {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(USERNAME_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
      sessionStorage.removeItem(DIAL_CODE_KEY);
    }
  }

  updateTokens(tokens: TokenResponse): void {
    const storage = this.getActiveStorage();
    if (!storage) {
      return;
    }

    const expiry = Date.now() + tokens.expires_in * 1000;
    storage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    storage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    storage.setItem(TOKEN_EXPIRY_KEY, String(expiry));
  }

  getAccessToken(): string | null {
    return this.getActiveStorage()?.getItem(ACCESS_TOKEN_KEY) ?? null;
  }

  getRefreshToken(): string | null {
    return this.getActiveStorage()?.getItem(REFRESH_TOKEN_KEY) ?? null;
  }

  getUsername(): string | null {
    return this.getActiveStorage()?.getItem(USERNAME_KEY) ?? null;
  }

  saveDialCode(dialCode: string): void {
    const normalized = this.normalizeDialCode(dialCode);
    if (!normalized) {
      return;
    }
    const storage = this.getActiveStorage();
    if (storage) {
      storage.setItem(DIAL_CODE_KEY, normalized);
    }
  }

  getDialCode(): string | null {
    return this.getActiveStorage()?.getItem(DIAL_CODE_KEY) ?? null;
  }

  isTokenExpired(): boolean {
    const expiry = this.getActiveStorage()?.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return true;
    }

    return Date.now() >= Number(expiry);
  }

  clear(): void {
    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(ACCESS_TOKEN_KEY);
      storage.removeItem(REFRESH_TOKEN_KEY);
      storage.removeItem(USERNAME_KEY);
      storage.removeItem(TOKEN_EXPIRY_KEY);
      storage.removeItem(DIAL_CODE_KEY);
    });
  }

  hasStoredSession(): boolean {
    return !!this.getAccessToken() && !!this.getRefreshToken();
  }

  private getActiveStorage(): Storage | null {
    if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) {
      return sessionStorage;
    }

    if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
      return localStorage;
    }

    return null;
  }

  private getStorage(rememberMe: boolean): Storage {
    return rememberMe ? localStorage : sessionStorage;
  }

  private normalizeDialCode(dialCode: string): string {
    const digits = dialCode.replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
}
